import { AnalyticsIdentifyEvent, AnalyticsGroupEvent } from './server-types';
import { Analytics, AnalyticsUser, PageEvent, TrackEvent } from './types';
import { logger } from '@modules/logger';
import { getCookie } from 'cookies-next';

import * as amplitude from '@amplitude/analytics-browser';

function getGaContext(): { sessionId: string | null } {
  return { sessionId: getCookie('_ga4') ?? '' };
}

const platform = 'dash';
const version = 1;

class AmplitudeAnalytics implements Analytics {
  private initialized = false;
  private identifyInstance: amplitude.Identify | null = null;

  constructor() {
    this.initAmplitude();
  }

  private initAmplitude(): void {
    if (this.initialized) return;

    const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_ANALYTICS_KEY;
    const serverUrl = process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL;

    if (!apiKey) {
      logger.warn('Amplitude API key is missing');
      return;
    }

    amplitude.init(apiKey, {
      defaultTracking: false,
      serverUrl: serverUrl ?? undefined,
    });

    this.initialized = true;
  }

  async initialize(): Promise<void> {
    return; // Optional async init hook
  }

  track(e: Partial<TrackEvent>): void {
    if (!e.name) {
      logger.warn('Amplitude track() called without name');
      return;
    }

    const eventProperties = {
      ...e.properties,
      platform,
      version,
      ga: getGaContext(),
      ...e.context,
    };

    try {
      amplitude.track(e.name, eventProperties);
    } catch (error) {
      logger.warn('Error calling Amplitude track()', { error });
    }
  }

  page(e: Partial<PageEvent>): void {
    // Segment adds the Viewed prefix to page_view events
    // Explicitly adding 'Viewed ' it here for consistency
    const eventName = 'Viewed page_view';
    const properties = {
      ...e.properties,
      host: e.properties?.host ?? window.location.hostname,
      platform,
      version,
      name: e.name ?? 'page_view',
      ga: getGaContext(),
      referrer: document.referrer,
      url: window.location.href,
      title: document.title,
    };

    try {
      amplitude.track(eventName, properties);
    } catch (error) {
      logger.warn('Error calling amplitude page()', { error });
    }
  }

  identify(e: Partial<AnalyticsIdentifyEvent>): void {
    try {
      const identify = new amplitude.Identify();
      if (e.traits) {
        for (const [key, value] of Object.entries(e.traits)) {
          if (value !== null && !Array.isArray(value)) {
            identify.set(key, value);
          }
        }
      }
      amplitude.setUserId(e.user_id);
      amplitude.identify(identify);
    } catch (error) {
      logger.error('Error in amplitude identify()', { error });
    }
  }

  group(e: Partial<AnalyticsGroupEvent>): void {}

  user(): AnalyticsUser | undefined {
    try {
      return {
        id: () => amplitude.getUserId(),
        anonymousId: () => amplitude.getDeviceId(),
      };
    } catch (error) {
      logger.warn('Amplitude user() error', { error });
      return undefined;
    }
  }

  reset(): void {
    try {
      amplitude.reset();
    } catch (error) {
      logger.warn('Error resetting Amplitude', { error });
    }
  }
}

export const amplitudeAnalytics = new AmplitudeAnalytics();
